import { useState, useCallback } from "react";
import { Stack, ToggleButtonGroup, ToggleButton } from "@mui/material";
import { Controller } from "react-hook-form";
import SearchableSelect from "@components/atoms/SearchableSelect/SearchableSelect";
import InputText from "@components/atoms/InputText/InputText";
import { clientService } from "@services/ClientService";
import { useFormValidate } from "@utils/hooks";
import { IClientNameInput } from "src/types/molecules";
import { IClient } from "src/types/global";
type ClientMode = "existing" | "free";
const ClientNameInput = ({
  label = "Cliente",
  // register is part of the public API (forwarded by the parent form) but
  // this molecule manages clientName internally via Controller + control.
  register: _register,
  setValue,
  control,
  disabled = false
}: IClientNameInput) => {
  const [mode, setMode] = useState<ClientMode>("existing");
  const {
    validateField
  } = useFormValidate();
  const handleModeChange = useCallback((_: React.MouseEvent<HTMLElement>, newMode: ClientMode | null) => {
    // ToggleButtonGroup enforces at-least-one with exclusive — guard anyway.
    if (!newMode) return;
    setMode(newMode);
    if (newMode === "free") {
      // Switching to free text: clear the linked clientId.
      setValue("clientId", undefined);
    } else {
      // Switching to existing client: clear both until a client is picked.
      setValue("clientName", "");
      setValue("clientId", undefined);
    }
  }, [setValue]);
  return <Stack spacing={1.5}>
      <ToggleButtonGroup value={mode} exclusive onChange={handleModeChange} size="small" disabled={disabled} aria-label="Modo de selección de cliente">
        <ToggleButton value="existing" aria-label="Cliente existente">
          Cliente existente
        </ToggleButton>
        <ToggleButton value="free" aria-label="Nombre libre">
          Nombre libre
        </ToggleButton>
      </ToggleButtonGroup>

      {/*
       * Controller registers clientName for validation in both modes.
       * In "existing" mode we intercept SearchableSelect's onChange to parse
       * the serialized client JSON and snapshot clientName + clientId.
       * In "free" mode we bind InputText directly to the RHF field.
       */}
      <Controller name="clientName" control={control} rules={{
      required: true,
      validate: value => Boolean(value) && value.trim().length > 0
    }} render={({
      field,
      fieldState
    }) => mode === "existing" ? <SearchableSelect label={label} placeholder="Selecciona un cliente" loading="Cargando clientes" errorMessage="Error al obtener los clientes" primaryKey="fullName" secondaryKey="identification" query={(rowsPerPage, search) => clientService.getAll(rowsPerPage, undefined, search)} register={({
      onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
        const serialized = e.target.value;
        try {
          const client: IClient = JSON.parse(serialized);
          if (client && !isNaN(client.id)) {
            setValue("clientId", client.id);
            field.onChange(client.fullName);
          }
        } catch {
          // Placeholder value "-1" is not valid JSON — ignore.
        }
      }
    } as any)} helperText={validateField(fieldState.error)} disabled={disabled} /> : <InputText label={label} name="clientName" placeholder="Nombre del cliente" register={({
      ...field
    } as any)} helperText={validateField(fieldState.error)} disabled={disabled} />} />
    </Stack>;
};
export default ClientNameInput;